/**
 * The Descent Board — the hub's run band (2026-10-09).
 *
 * User picked "C · The Descent Board" from three Claude Design alternatives
 * of the Sconce, after asking that "all elements be animated and as
 * interactive as possible". The hub stops being a grid of six equal tiles
 * with Play as the seventh, and becomes one band that answers "where am I
 * going next" — mode tabs, what the mode is, the floor it starts on, and a
 * Play you hold — with the six stations docked underneath.
 *
 * Choosing a mode or a floor here moves the same state the Play panel moves
 * (`selectedGameMode`, `selectedContractId`); the panel is still where the
 * rite, the energy line and every contract's Buy live. This band only ever
 * picks between things the player already has, and opens the panel for
 * anything that would spend.
 *
 * Pure string builders, like the rest of `ui/`. Class names are literals —
 * Tailwind extracts statically — and the band's own look lives in app.css
 * under "The Descent Board".
 */

import type { GameModeRow } from '../net/game-modes-api';
import { isPlayable } from '../net/game-modes-api';
import type { MapOptionRow } from '../net/contracts-api';
import { mapTileStatus, usableContractsOn, difficultyPips, type MapCardState } from './map-picker';
import { modeUsesContracts } from './play-flow';
import { esc } from './item-icon';
import { t } from '../i18n/i18n';

/** Mode tabs. A mode with no room behind it stays visible and says so, as the Play panel's cards do. */
export function bandModeTabsMarkup(modes: readonly GameModeRow[], selectedId: string, locked: boolean): string {
  return modes.map((m) => {
    const playable = isPlayable(m);
    const on = m.id === selectedId;
    const disabled = !playable || locked ? ' disabled' : '';
    const soon = playable ? '' : `<span class="band-tab-soon">${esc(t('Soon'))}</span>`;
    return `<button type="button" role="tab" data-band-mode="${esc(m.id)}" aria-selected="${on}"${disabled}
      class="band-tab${on ? ' band-tab-on' : ''}">${esc(t(m.name))}${soon}</button>`;
  }).join('');
}

/** The mode's name, line and description — re-keyed per mode so the swap animation restarts. */
export function bandModeCopyMarkup(mode: GameModeRow | undefined): string {
  if (!mode) return '';
  return `
    <div class="band-swap" data-band-copy="${esc(mode.id)}">
      <h1 class="band-title">${esc(t(mode.name))}</h1>
      <p class="band-tagline">${esc(t(mode.tagline))}</p>
      <p class="band-desc">${esc(t(mode.description))}</p>
    </div>`;
}

/** The art behind the band: the floor you are about to start on for a Delve, the mode's own art otherwise. */
export function bandArtUrl(mode: GameModeRow | undefined, catalog: readonly MapOptionRow[], state: MapCardState): string {
  if (mode && modeUsesContracts(mode.id)) {
    const active = catalog.find((o) => mapTileStatus(o, state).tone === 'gold');
    if (active) return active.stratum.preview;
  }
  return `/assets/ui/lobby/${mode?.art ?? 'tile-delve'}.webp`;
}

/** Which floor the gauge's lantern hangs at; -1 when none is active (should not happen — the Cellars are free). */
export function activeFloorIndex(catalog: readonly MapOptionRow[], state: MapCardState): number {
  return catalog.findIndex((o) => mapTileStatus(o, state).tone === 'gold');
}

export type FloorTap =
  | { kind: 'select'; contractId: string | null }
  | { kind: 'open' };

/**
 * What tapping a floor on the gauge does.
 *
 * Selects outright only when there is exactly one way in the player already
 * has — the free entry, or a single owned contract — because that is the only
 * case with nothing to decide. Anything else (several owned cards, nothing
 * owned, or the floor already chosen) opens that floor in the Contracts panel,
 * where the choice and the prices can be read.
 */
export function floorTapAction(option: MapOptionRow, state: MapCardState): FloorTap {
  if (mapTileStatus(option, state).tone === 'gold') return { kind: 'open' };
  const owned = usableContractsOn(option, state.contracts);
  if (owned.length === 0 && option.freeEntry) return { kind: 'select', contractId: null };
  if (owned.length === 1 && !option.freeEntry) return { kind: 'select', contractId: owned[0]!.id };
  return { kind: 'open' };
}

/** The floor gauge: a chain down the band's right edge, one stop per floor, the lantern at the chosen one. */
export function floorGaugeMarkup(catalog: readonly MapOptionRow[], state: MapCardState, modeId: string): string {
  const inert = !modeUsesContracts(modeId);
  const rows = catalog.map((option) => {
    const status = mapTileStatus(option, state);
    const on = status.tone === 'gold';
    return `
      <button type="button" data-band-floor="${esc(option.stratum.id)}" aria-pressed="${on}"
        class="band-floor${on ? ' band-floor-on' : ''}">
        <img src="${esc(option.stratum.preview)}" alt="" loading="lazy" draggable="false" class="band-floor-art" />
        <span class="min-w-0 flex-1 text-left">
          <span class="block font-heading text-[15px] leading-tight text-text">${esc(t(option.stratum.name))}</span>
          <span class="block text-[12px] ${on ? 'text-gold-bright' : 'text-text-dim'}">${esc(status.text)}</span>
        </span>
        ${difficultyPips(option.stratum.depth)}
      </button>`;
  }).join('');
  const index = activeFloorIndex(catalog, state);
  // The lantern's stop is set as a custom property, not a class, so one rule
  // places it for any number of floors.
  const lantern = index >= 0 ? `<span class="band-lantern" aria-hidden="true" style="--stop:${index}"></span>` : '';
  const note = inert
    ? `<p class="text-[12px] text-text-dim leading-snug mt-1">${esc(t('Floors only apply to a Delve.'))}</p>`
    : '';
  return `<div class="band-gauge${inert ? ' band-gauge-inert' : ''}" role="group" aria-label="${esc(t('Floor'))}">${lantern}${rows}</div>${note}`;
}
